import { Flex, Skeleton, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { MeetingCalendar } from './meeting-calendar';
import groupBy from 'lodash/groupBy';
import { dateFormatter } from 'app/helpers/format-helper';
import { MeetingsList } from './meetings-list';
import { PastSessionsList } from './past-sessions-list';
import { useGetMeetingsQuery } from 'app/uconnect/queries/enhanced';
import { isPastMeeting } from 'app/uconnect/helpers/utils';
import { FetchedMeeting } from 'app/uconnect/helpers/types';
import auth from '@udacity/ureact-hoth';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

const PAST_TAB_INDEX = 0;
const CALENDAR_TAB_INDEX = 1;
const PAST_LOOKBACK_MONTHS = 6;

// Stable reference so memoized derivations don't recompute every render while loading.
const EMPTY_MEETINGS: FetchedMeeting[] = [];

export const Meetings: React.FC = () => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(PAST_TAB_INDEX);
  const [focusedDay, setFocusedDay] = useState<string | undefined>();
  const [focusedMonth, setFocusedMonth] = useState<Dayjs>(dayjs());

  const facilitatorUserKey = auth.getCurrentUserId();

  const { data, isLoading } = useGetMeetingsQuery(
    {
      facilitatorUserKey,
      startsBefore: focusedMonth.endOf('month').toISOString(),
      startsAfter: focusedMonth.startOf('month').toISOString(),
    },
    { skip: tabIndex !== CALENDAR_TAB_INDEX }
  );

  // Recompute the window once per calendar day (keyed on today) rather than freezing it
  // to mount time, so sessions that start on later days still appear once they've ended.
  // Rounding to day boundaries keeps the value stable within a day so RTK Query doesn't
  // refetch in an infinite loop.
  const today = dayjs().format('YYYY-MM-DD');
  const pastRange = useMemo(
    () => ({
      startsBefore: dayjs().endOf('day').toISOString(),
      startsAfter: dayjs().subtract(PAST_LOOKBACK_MONTHS, 'month').startOf('day').toISOString(),
    }),
    [today]
  );

  const { data: pastData, isLoading: isPastLoading } = useGetMeetingsQuery(
    {
      facilitatorUserKey,
      startsBefore: pastRange.startsBefore,
      startsAfter: pastRange.startsAfter,
    },
    { skip: tabIndex !== PAST_TAB_INDEX }
  );

  const meetings = data?.meetings?.meetings ?? EMPTY_MEETINGS;

  const pastMeetings = useMemo(
    () => (pastData?.meetings?.meetings ?? EMPTY_MEETINGS).filter(isPastMeeting),
    [pastData?.meetings?.meetings]
  );

  const handleSelectDate = (date: Date) => {
    const selectedDay = dateFormatter.numeric(date);

    if (focusedDay === selectedDay || meetingDays.indexOf(selectedDay) < 0) {
      // if the user clicks the same day twice - remove the filter
      // OR if the user clicks a random day without meetings - remove the filter
      setFocusedDay(undefined);
    } else {
      // filter the meetings list to the selected day
      setFocusedDay(selectedDay);
    }
  };

  const handleSelectMonth = (date: Dayjs) => {
    setFocusedMonth(date);
    setFocusedDay(undefined);
  };

  const allMeetingsByDay = useMemo(
    () => groupBy(meetings, (meeting) => dateFormatter.numeric(meeting.startAt)),
    [meetings]
  );

  const meetingDays = useMemo(() => Object.keys(allMeetingsByDay), [allMeetingsByDay]);

  const meetingsByDay = useMemo(() => {
    return focusedDay ? { [focusedDay]: allMeetingsByDay[focusedDay] } : allMeetingsByDay;
  }, [allMeetingsByDay, focusedDay]);

  return (
    <Tabs index={tabIndex} onChange={setTabIndex} isLazy>
      <TabList>
        <Tab>{t('connect.pastSessions')}</Tab>
        <Tab>{t('connect.calendar')}</Tab>
      </TabList>
      <TabPanels>
        <TabPanel px={0}>
          <Skeleton isLoaded={!isPastLoading}>
            <PastSessionsList meetings={pastMeetings} />
          </Skeleton>
        </TabPanel>
        <TabPanel px={0}>
          <Skeleton isLoaded={!isLoading}>
            <Flex gap={8}>
              <MeetingCalendar
                datesToHighlight={meetingDays}
                onSelectDate={handleSelectDate}
                onSelectMonth={handleSelectMonth}
              />
              <MeetingsList meetingsByDay={meetingsByDay} />
            </Flex>
          </Skeleton>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
